import React, { useState, useEffect } from 'react';
import { API, fetchSites as utilFetchSites } from '../utils/api';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import AddSiteModal from './AddSiteModal';
import EditSiteModal from './EditSiteModal';
import ConfirmModal from './modals/ConfirmModal';
import SuccessModal from './modals/SuccessModal';

// SortIcon component moved outside to prevent re-creation on each render
const SortIcon = ({ field, sortField, sortDirection }) => {
  if (sortField !== field) {
    return (
      <svg className="w-4 h-4 text-gray-500 ml-1" fill="currentColor" viewBox="0 0 20 20">
        <path d="M5 12l5-5 5 5H5z"/>
      </svg>
    );
  }
  
  return sortDirection === 'asc' ? (
    <svg className="w-4 h-4 text-purple-400 ml-1" fill="currentColor" viewBox="0 0 20 20">
      <path d="M5 12l5-5 5 5H5z"/>
    </svg>
  ) : (
    <svg className="w-4 h-4 text-purple-400 ml-1" fill="currentColor" viewBox="0 0 20 20">
      <path d="M15 8l-5 5-5-5h10z"/>
    </svg>
  );
};

const SiteManagement = ({ sites, fetchSites }) => {
  const location = useLocation();
  const [showAddSiteModal, setShowAddSiteModal] = useState(false);
  const [showEditSiteModal, setShowEditSiteModal] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [sortField, setSortField] = useState('site_id');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, site: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [successModal, setSuccessModal] = useState({ show: false, message: '', needsRefresh: false });
  const [toggleConfirm, setToggleConfirm] = useState({ show: false, siteId: null, currentStatus: null, siteName: null });
  
  // Archive state
  const [showArchived, setShowArchived] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState({ show: false, site: null });
  const [isArchiving, setIsArchiving] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, message: '' });
  
  // Thumbnail hover preview state
  const [hoveredThumbnail, setHoveredThumbnail] = useState(null);
  const [thumbnailPosition, setThumbnailPosition] = useState({ x: 0, y: 0 });
  
  // Subscription limit state
  const [siteLimit, setSiteLimit] = useState(null);
  const [currentSiteCount, setCurrentSiteCount] = useState(0);

  // Local sites state for including archived
  const [localSites, setLocalSites] = useState(sites);
  const [archivedCount, setArchivedCount] = useState(0);
  
  // Helper to check if unlimited (case-insensitive)
  const isUnlimited = (limit) => {
    return limit && limit.toString().toLowerCase() === 'unlimited';
  };

  // Fetch subscription limits
  useEffect(() => {
    const fetchSubscriptionLimits = async () => {
      try {
        // Fetch organisation data which NOW includes subscription_features
        const orgResponse = await axios.get(`${API}/admin/organisation`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        const org = orgResponse.data;
        const features = org.subscription_features || {};
        
        // Get max sites directly from subscription features (NO HARDCODED VALUES!)
        setSiteLimit(features.maxSites || '0');
        
        // Count active sites
        const activeSites = sites.filter(s => s.status !== 'inactive').length;
        setCurrentSiteCount(activeSites);
        
      } catch (error) {
        console.error('Error fetching subscription limits:', error);
      }
    };
    
    fetchSubscriptionLimits();
  }, [sites]);

  // Fetch sites with archive option
  const fetchSitesWithArchive = async () => {
    try {
      const response = await axios.get(`${API}/admin/sites`, {
        params: { include_archived: showArchived },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setLocalSites(response.data);
      
      // Also get archived count for badge
      if (!showArchived) {
        const archivedResponse = await axios.get(`${API}/admin/sites`, {
          params: { include_archived: true },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const archived = archivedResponse.data.filter(s => s.is_archived);
        setArchivedCount(archived.length);
      } else {
        const archived = response.data.filter(s => s.is_archived);
        setArchivedCount(archived.length);
      }
    } catch (error) {
      console.error('Failed to fetch sites:', error);
    }
  };

  // Fetch when showArchived changes
  useEffect(() => {
    fetchSitesWithArchive();
  }, [showArchived]);

  // Update localSites when props sites change
  useEffect(() => {
    if (!showArchived) {
      setLocalSites(sites);
    }
  }, [sites, showArchived]);

  // Separate useEffect for handling auto-edit functionality
  useEffect(() => {
    if (sites.length === 0) return;
    
    // Check URL parameters first
    const urlParams = new URLSearchParams(location.search);
    const urlSiteId = urlParams.get('site_id');
    
    // Check localStorage as fallback
    const localSiteId = localStorage.getItem('editSiteId');
    
    const siteIdToEdit = urlSiteId || localSiteId;
    
    if (siteIdToEdit) {
      // Clear localStorage if it was used
      if (localSiteId) {
        localStorage.removeItem('editSiteId');
      }
      
      // Find and edit the site (search by both id and site_id for compatibility)
      const siteToEdit = sites.find(site => site.id === siteIdToEdit || site.site_id === siteIdToEdit);
      if (siteToEdit) {
        handleEditSite(siteToEdit);
      }
    }
  }, [sites, location.search]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Helper function to get full address from structured fields
  const getFullAddress = (site) => {
    const parts = [
      site.address_line_1,
      site.address_line_2,
      site.city,
      site.postcode,
      site.country
    ].filter(p => p && p.trim());
    return parts.join(', ');
  };

  const getSortedSites = () => {
    const filteredSites = localSites.filter(site => {
      const fullAddress = getFullAddress(site);
      return site.site_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        site.site_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (site.description && site.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (site.city && site.city.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (fullAddress && fullAddress.toLowerCase().includes(searchTerm.toLowerCase()));
    });
    
    return filteredSites.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  const handleEditSite = (site) => {
    setEditingSite(site);
    setShowEditSiteModal(true);
  };

  const cancelEditSite = () => {
    setEditingSite(null);
    setShowEditSiteModal(false);
  };

  const [checkingDelete, setCheckingDelete] = useState(false);

  const handleDeleteSite = async (site) => {
    // Check if site can be deleted before showing modal
    setCheckingDelete(true);
    try {
      const response = await axios.get(`${API}/admin/sites/${site.site_id}/can-delete`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const { can_delete, must_archive, clock_record_count } = response.data;
      
      if (must_archive) {
        // Site has clock records - show archive modal directly
        setArchiveConfirm({ 
          show: true, 
          site,
          clockRecordCount: clock_record_count 
        });
      } else if (can_delete) {
        // Site can be deleted - show delete confirmation
        setDeleteConfirm({ show: true, site });
      } else {
        // Site is already archived or other issue
        setErrorModal({
          show: true,
          message: response.data.reason || 'Cannot delete this site.'
        });
      }
    } catch (error) {
      console.error('Error checking delete eligibility:', error);
      // Fallback to showing delete modal - backend will validate again
      setDeleteConfirm({ show: true, site });
    } finally {
      setCheckingDelete(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.site) return;
    
    const siteName = deleteConfirm.site.site_name;
    setIsDeleting(true);
    try {
      const response = await axios.delete(`${API}/admin/sites/${deleteConfirm.site.site_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Close delete modal
      setDeleteConfirm({ show: false, site: null });
      setIsDeleting(false);
      
      // Show success modal with details
      const totalDeleted = response.data.total_records_deleted || 0;
      setSuccessModal({ 
        show: true, 
        message: `Site "${siteName}" has been permanently deleted.\n\n${totalDeleted} related record(s) removed.`,
        needsRefresh: true
      });
      
    } catch (error) {
      console.error('Error deleting site:', error);
      setIsDeleting(false);
      setDeleteConfirm({ show: false, site: null });
      
      const errorDetail = error.response?.data?.detail || 'Failed to delete site. Please try again.';
      setErrorModal({
        show: true,
        message: errorDetail
      });
    }
  };

  // Archive handlers
  const handleArchiveSite = (site) => {
    setArchiveConfirm({ show: true, site });
  };

  const handleArchiveConfirm = async () => {
    if (!archiveConfirm.site) return;
    
    const siteName = archiveConfirm.site.site_name;
    setIsArchiving(true);
    try {
      const response = await axios.post(
        `${API}/admin/sites/${archiveConfirm.site.site_id}/archive`,
        null,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      setArchiveConfirm({ show: false, site: null });
      setIsArchiving(false);
      
      const clockRecords = response.data.clock_records_preserved || 0;
      setSuccessModal({ 
        show: true, 
        message: `Site "${siteName}" has been archived.\n\n${clockRecords} clock record(s) preserved for historical reporting.`,
        needsRefresh: true
      });
      
    } catch (error) {
      console.error('Error archiving site:', error);
      setIsArchiving(false);
      setArchiveConfirm({ show: false, site: null });
      setErrorModal({
        show: true,
        message: error.response?.data?.detail || 'Failed to archive site. Please try again.'
      });
    }
  };

  const handleUnarchiveSite = async (site) => {
    try {
      await axios.post(
        `${API}/admin/sites/${site.site_id}/unarchive`,
        null,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      setSuccessModal({ 
        show: true, 
        message: `Site "${site.site_name}" has been restored.`,
        needsRefresh: true
      });
      
    } catch (error) {
      console.error('Error unarchiving site:', error);
      setErrorModal({
        show: true,
        message: error.response?.data?.detail || 'Failed to restore site. Please try again.'
      });
    }
  };

  // Handle success modal close with refresh
  const handleSuccessModalClose = () => {
    const needsRefresh = successModal.needsRefresh;
    setSuccessModal({ show: false, message: '', needsRefresh: false });
    
    // Refresh sites list after modal is closed
    if (needsRefresh) {
      console.log('Refreshing sites list after modal close');
      fetchSitesWithArchive();
    }
  };

  const handleToggleStatus = (site) => {
    // Prevent toggling archived sites
    if (site.is_archived) {
      setErrorModal({
        show: true,
        message: 'Cannot change status of an archived site. Unarchive it first.'
      });
      return;
    }
    setToggleConfirm({ 
      show: true, 
      siteId: site.site_id, 
      currentStatus: site.status,
      siteName: site.site_name
    });
  };

  const handleToggleConfirm = async () => {
    if (!toggleConfirm.siteId) return;
    
    const { siteId, currentStatus, siteName } = toggleConfirm;
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    const action = newStatus === 'active' ? 'activate' : 'deactivate';
    
    try {
      await axios.patch(`${API}/admin/sites/${siteId}/toggle-status`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Close toggle confirmation modal
      setToggleConfirm({ show: false, siteId: null, currentStatus: null, siteName: null });
      
      // Show success modal
      setSuccessModal({ 
        show: true, 
        message: `Site "${siteName}" has been ${action}d successfully. ${newStatus === 'inactive' ? 'Users will not be able to clock in to this site.' : 'Users can now clock in to this site.'}`,
        needsRefresh: true
      });
      
    } catch (error) {
      console.error('Error toggling site status:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to update site status. Please try again.';
      // Close toggle modal first
      setToggleConfirm({ show: false, siteId: null, currentStatus: null, siteName: null });
      // Show error modal
      setErrorModal({
        show: true,
        message: `Error: ${errorMessage}`
      });
    }
  };

  // Check if limit is reached
  const limitReached = siteLimit !== 'Unlimited' && siteLimit && currentSiteCount >= parseInt(siteLimit);

  return (
    <>
      <div className="min-h-screen bg-slate-900 -m-8 p-8">
        {/* Modern Header - Purple Gradient (matching Tasks page) */}
        <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-6 text-white mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold mb-1">Sites</h1>
              <p className="text-purple-200">Create, edit and manage sites across your organization</p>
            </div>
            <button
              onClick={() => setShowAddSiteModal(true)}
              disabled={limitReached}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl font-medium ${
                limitReached
                  ? 'bg-gray-500 text-gray-300 cursor-not-allowed'
                  : 'bg-purple-500 hover:bg-purple-400'
              }`}
              title={limitReached ? 'Site limit reached. Upgrade your plan to add more sites.' : 'Add a new site'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>{limitReached ? 'Limit Reached' : 'Add Site'}</span>
            </button>
          </div>
        </div>
        
        <div className="bg-slate-800 rounded-xl shadow-lg border border-slate-700">
          {/* Subscription Info Note */}
          {siteLimit && (
            <div className="px-6 py-4 border-b border-slate-700">
              <div className={`p-3 rounded-lg border ${
                isUnlimited(siteLimit)
                  ? 'bg-green-500/20 border-green-500/30'
                  : currentSiteCount >= parseInt(siteLimit)
                  ? 'bg-red-500/20 border-red-500/30'
                  : (currentSiteCount / parseInt(siteLimit)) >= 0.5
                  ? 'bg-yellow-500/20 border-yellow-500/30'
                  : 'bg-blue-500/20 border-blue-500/30'
              }`}>
                <div className="flex items-start">
                  <svg className={`w-5 h-5 mt-0.5 mr-2 ${
                    isUnlimited(siteLimit)
                      ? 'text-green-400'
                      : currentSiteCount >= parseInt(siteLimit)
                      ? 'text-red-400'
                      : (currentSiteCount / parseInt(siteLimit)) >= 0.5
                      ? 'text-yellow-400'
                      : 'text-blue-400'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className={`text-sm font-medium ${
                      isUnlimited(siteLimit)
                        ? 'text-green-300'
                        : currentSiteCount >= parseInt(siteLimit)
                        ? 'text-red-300'
                        : (currentSiteCount / parseInt(siteLimit)) >= 0.5
                        ? 'text-yellow-300'
                        : 'text-blue-300'
                    }`}>
                      {isUnlimited(siteLimit) ? (
                        `You have ${currentSiteCount} active site${currentSiteCount !== 1 ? 's' : ''}. Your current plan includes unlimited sites.`
                      ) : currentSiteCount >= parseInt(siteLimit) ? (
                        `You have ${currentSiteCount} active site${currentSiteCount !== 1 ? 's' : ''}. Your current plan allows for a maximum of ${siteLimit} active site${parseInt(siteLimit) !== 1 ? 's' : ''}. You need to make one of your currently active sites inactive to add a new one.`
                      ) : (currentSiteCount / parseInt(siteLimit)) >= 0.5 ? (
                        `You have ${currentSiteCount} active site${currentSiteCount !== 1 ? 's' : ''}. Your current plan allows for a maximum of ${siteLimit} active site${parseInt(siteLimit) !== 1 ? 's' : ''}. Once you reach this limit, you will need to make one of your currently active sites inactive to add a new one.`
                      ) : (
                        `You have ${currentSiteCount} active site${currentSiteCount !== 1 ? 's' : ''}. Your current plan allows for a maximum of ${siteLimit} active site${parseInt(siteLimit) !== 1 ? 's' : ''}.`
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Search Bar */}
          <div className="px-6 py-4 border-b border-slate-700">
            <div className="flex items-center">
              <div className="relative flex-1 max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                  <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"></path>
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search sites..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              {searchTerm && (
                <div className="ml-3 text-sm text-gray-400">
                  {getSortedSites().length} of {localSites.length} sites
                  {archivedCount > 0 && !showArchived && (
                    <span className="ml-2 text-amber-400">({archivedCount} archived)</span>
                  )}
                </div>
              )}
              
              {/* Show Archived Toggle */}
              <div className="ml-4 flex items-center gap-2">
                <span className="text-sm text-gray-400">Show Archived</span>
                <button
                  onClick={() => setShowArchived(!showArchived)}
                  className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-slate-800 ${
                    showArchived ? 'bg-amber-500' : 'bg-slate-600'
                  }`}
                >
                  <span
                    className={`inline-block w-4 h-4 transform bg-white rounded-full shadow-md transition-transform ${
                      showArchived ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-4 px-6 font-semibold text-gray-400 text-sm w-16"></th>
                  <th 
                    className="text-left py-4 px-6 font-semibold text-gray-400 text-sm cursor-pointer hover:text-white select-none"
                    onClick={() => handleSort('site_name')}
                  >
                    <div className="flex items-center">
                      NAME
                      <SortIcon field="site_name" sortField={sortField} sortDirection={sortDirection} />
                    </div>
                  </th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-400 text-sm">DESCRIPTION</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-400 text-sm">ADDRESS</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-400 text-sm">STATUS</th>
                  <th className="text-center py-4 px-6 font-semibold text-gray-400 text-sm">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {getSortedSites().map((site, index) => (
                  <tr 
                    key={site.id} 
                    className={`transition-colors ${site.is_archived ? 'opacity-60 bg-slate-800/50' : 'hover:bg-slate-700/50 cursor-pointer'} ${index !== getSortedSites().length - 1 ? "border-b border-slate-700/50" : ""}`}
                    onClick={() => !site.is_archived && handleEditSite(site)}
                    data-testid={`site-row-${site.site_id}`}
                  >
                    <td className="py-4 px-6">
                      {site.thumbnail_url ? (
                        <div 
                          className="relative w-10 h-10 flex-shrink-0"
                          onMouseEnter={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setThumbnailPosition({ x: rect.right + 10, y: rect.top });
                            setHoveredThumbnail(site.thumbnail_url);
                          }}
                          onMouseLeave={() => setHoveredThumbnail(null)}
                        >
                          <img 
                            src={site.thumbnail_url} 
                            alt={site.site_name}
                            className={`w-10 h-10 rounded-lg object-cover border transition-colors ${site.is_archived ? 'border-slate-600 grayscale' : 'border-slate-600 cursor-pointer hover:border-purple-500'}`}
                          />
                        </div>
                      ) : (
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${site.is_archived ? 'bg-slate-600' : 'bg-gradient-to-br from-purple-500 to-indigo-600'}`}>
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-6a1 1 0 00-1-1H9a1 1 0 00-1 1v6a1 1 0 01-1 1H4a1 1 0 110-2V4z" clipRule="evenodd"></path>
                          </svg>
                        </div>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${site.is_archived ? 'text-gray-400' : 'text-white'}`}>{site.site_name}</span>
                        {site.is_archived && (
                          <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full border border-amber-500/30">
                            Archived
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6 text-gray-300">
                      {site.description || (
                        <span className="text-gray-500 italic">No description</span>
                      )}
                    </td>
                    <td className="py-4 px-6 text-gray-300">
                      {getFullAddress(site) ? (
                        <div className="text-sm">{getFullAddress(site)}</div>
                      ) : (
                        <span className="text-gray-500 italic text-sm">No address set</span>
                      )}
                    </td>
                    <td className="py-4 px-6" onClick={(e) => e.stopPropagation()}>
                      {site.is_archived ? (
                        <div className="text-xs text-gray-500 italic">Archived</div>
                      ) : (
                        <>
                          <label className="relative inline-block w-11 h-6" data-testid={`toggle-site-status-${site.site_id}`}>
                            <input
                              type="checkbox"
                              checked={site.status === 'active'}
                              onChange={() => handleToggleStatus(site)}
                              className="sr-only"
                            />
                            <div className={`block w-11 h-6 rounded-full transition-colors cursor-pointer ${
                              site.status === 'active' 
                                ? 'bg-green-500 hover:bg-green-400' 
                                : 'bg-slate-600 hover:bg-slate-500'
                            }`}>
                              <div className={`dot absolute left-1 top-1 w-4 h-4 rounded-full transition-transform bg-white ${
                                site.status === 'active' 
                                  ? 'transform translate-x-5' 
                                  : ''
                              }`}></div>
                            </div>
                          </label>
                          <div className="text-xs text-gray-400 mt-1">
                            {site.status === 'active' ? 'Active' : 'Inactive'}
                          </div>
                        </>
                      )}
                    </td>
                    <td className="py-4 px-6" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-2">
                        {site.is_archived ? (
                          /* Unarchive button for archived sites */
                          <button
                            onClick={() => handleUnarchiveSite(site)}
                            className="p-2 rounded-lg text-green-300 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 transition-colors"
                            title="Restore site"
                            data-testid={`unarchive-site-${site.site_id}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </button>
                        ) : (
                          /* Edit and Delete/Archive buttons for active sites */
                          <>
                            <button
                              onClick={() => handleEditSite(site)}
                              className="p-2 rounded-lg text-purple-300 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 transition-colors"
                              title="Edit site"
                              data-testid={`edit-site-${site.site_id}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteSite(site); }}
                              disabled={checkingDelete}
                              className={`p-2 rounded-lg text-red-300 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 transition-colors ${checkingDelete ? 'opacity-50 cursor-wait' : ''}`}
                              title="Delete site"
                              data-testid={`delete-site-${site.site_id}`}
                            >
                              {checkingDelete ? (
                                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              )}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {getSortedSites().length === 0 && (
                  <tr>
                    <td colSpan="6" className="py-8 text-center text-gray-500">
                      {searchTerm ? `No sites found matching "${searchTerm}"` : 'No sites found. Create a new site to get started.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add Site Modal */}
      <AddSiteModal
        isOpen={showAddSiteModal}
        onClose={() => setShowAddSiteModal(false)}
        onSiteCreated={(newSite) => {
          fetchSites();
        }}
      />

      {/* Edit Site Modal */}
      <EditSiteModal
        isOpen={showEditSiteModal}
        onClose={() => {
          setShowEditSiteModal(false);
          setEditingSite(null);
        }}
        site={editingSite}
        onSiteUpdated={(updatedSite) => {
          fetchSites();
        }}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirm.show}
        onClose={() => setDeleteConfirm({ show: false, site: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Site?"
        message={`Are you sure you want to delete "${deleteConfirm.site?.site_name}"? This action cannot be undone and will remove all associated data.`}
        confirmText="Delete Site"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Archive Confirmation Modal */}
      <ConfirmModal
        isOpen={archiveConfirm.show}
        onClose={() => setArchiveConfirm({ show: false, site: null, clockRecordCount: 0 })}
        onConfirm={handleArchiveConfirm}
        title="Archive Site?"
        message={archiveConfirm.clockRecordCount > 0 
          ? `This site has ${archiveConfirm.clockRecordCount} clock record(s) and cannot be deleted. Archive it instead.\n\nArchiving "${archiveConfirm.site?.site_name}" will:\n• Set the site to inactive\n• Remove all user assignments\n• Preserve all historical data\n\nYou can restore the site later if needed.`
          : `Are you sure you want to archive "${archiveConfirm.site?.site_name}"?\n\nThis will:\n• Set the site to inactive\n• Remove all user assignments\n• Preserve historical data (clock records, reports)\n\nYou can restore the site later if needed.`
        }
        confirmText="Archive Site"
        cancelText="Cancel"
        variant="warning"
        isLoading={isArchiving}
      />

      {/* Toggle Status Confirmation Modal */}
      <ConfirmModal
        isOpen={toggleConfirm.show}
        onClose={() => setToggleConfirm({ show: false, siteId: null, currentStatus: null, siteName: null })}
        onConfirm={handleToggleConfirm}
        title={toggleConfirm.currentStatus === 'active' ? 'Deactivate Site?' : 'Activate Site?'}
        message={
          toggleConfirm.currentStatus === 'active' 
            ? `Are you sure you want to deactivate "${toggleConfirm.siteName}"? Users will not be able to clock in to this site while it is inactive.`
            : `Are you sure you want to activate "${toggleConfirm.siteName}"? Users will be able to clock in to this site.`
        }
        confirmText={toggleConfirm.currentStatus === 'active' ? 'Deactivate' : 'Activate'}
        cancelText="Cancel"
        variant={toggleConfirm.currentStatus === 'active' ? 'warning' : 'info'}
      />

      {/* Success Modal */}
      <SuccessModal
        isOpen={successModal.show}
        onClose={handleSuccessModalClose}
        title="Success"
        message={successModal.message}
      />

      {/* Error Modal */}
      {errorModal.show && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl shadow-2xl border border-slate-600 p-6 max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-500/20 rounded-full">
                <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white">Error</h3>
            </div>
            <p className="text-gray-300 mb-6 whitespace-pre-line">{errorModal.message}</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setErrorModal({ show: false, message: '' })}
                className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Thumbnail Hover Preview */}
      {hoveredThumbnail && (
        <div 
          className="fixed z-50 pointer-events-none"
          style={{
            left: Math.min(thumbnailPosition.x, window.innerWidth - 420),
            top: Math.max(10, Math.min(thumbnailPosition.y - 80, window.innerHeight - 420))
          }}
        >
          <div className="bg-slate-800 rounded-xl shadow-2xl border border-slate-600 p-2 animate-in fade-in zoom-in-95 duration-150">
            <img 
              src={hoveredThumbnail} 
              alt="Site thumbnail preview"
              className="w-96 h-96 rounded-lg object-cover"
            />
          </div>
        </div>
      )}
    </>
  );
};


export default SiteManagement;
